/* This component owns only its own carousel, links, timers and editor listeners. */
if (!customElements.get('ro-product')) {
  customElements.define('ro-product', class extends HTMLElement {
    connectedCallback() {
      this.teardown();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.viewport = this.querySelector('.mos-battery__viewport');
      if (!this.viewport) return;
      this.originals = [...this.querySelectorAll('.mos-battery__slide')];
      this.count = this.originals.length;
      this.index = Math.min(Math.max(0, Number(this.dataset.initialIndex) || 0), this.count - 1);
      this.desktop = matchMedia('(min-width: 1024px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.prev = this.querySelector('.mos-battery__arrow--prev');
      this.next = this.querySelector('.mos-battery__arrow--next');
      this.dots = this.querySelector('.mos-battery__dots');
      this.hovered = false;
      this.dragging = false;
      this.suppressUntil = 0;
      this.makeDots();
      this.prev?.addEventListener('click', () => this.move(-1), options);
      this.next?.addEventListener('click', () => this.move(1), options);
      this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { this.hovered = true; this.pause(); } }, options);
      this.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { this.hovered = false; this.schedule(); } }, options);
      this.addEventListener('focusin', () => this.pause(), options);
      this.addEventListener('focusout', () => queueMicrotask(() => this.schedule()), options);
      this.addEventListener('keydown', (e) => this.keydown(e), options);
      this.viewport.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        this.pointerStart = { x: e.clientX, y: e.clientY, id: e.pointerId };
        this.dragging = true;
        this.dragMoved = false;
        this.pause();
      }, options);
      window.addEventListener('pointermove', (e) => {
        if (!this.pointerStart || e.pointerId !== this.pointerStart.id) return;
        if (Math.abs(e.clientX - this.pointerStart.x) > 7 || Math.abs(e.clientY - this.pointerStart.y) > 7) {
          this.dragMoved = true;
          this.classList.add('is-dragging');
        }
      }, options);
      const finish = () => {
        if (this.dragMoved) this.suppressUntil = performance.now() + 350;
        this.pointerStart = null;
        this.dragMoved = false;
        this.dragging = false;
        this.classList.remove('is-dragging');
        this.schedule();
      };
      window.addEventListener('pointerup', finish, options);
      window.addEventListener('pointercancel', finish, options);
      this.addEventListener('click', (e) => {
        if (!e.target.closest('.mos-battery__card')) return;
        if (performance.now() < this.suppressUntil || this.swiper?.allowClick === false) {
          e.preventDefault(); e.stopImmediatePropagation();
        }
      }, { ...options, capture: true });
      this.addEventListener('click', (e) => {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.target.closest('a,button')) return;
        const card = e.target.closest('.mos-battery__card');
        if (card?.dataset.productUrl) window.location.assign(card.dataset.productUrl);
      }, options);
      this.viewport.addEventListener('dragstart', (e) => e.preventDefault(), options);
      this.desktop.addEventListener('change', () => this.build(), options);
      this.reduced.addEventListener('change', () => this.build(), options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      document.addEventListener('shopify:block:select', (e) => {
        const card = e.target.closest('.mos-battery__slide');
        if (!card || !this.contains(card)) return;
        this.editorSelected = true;
        this.pause();
        this.go(Number(card.dataset.cardIndex));
      }, options);
      document.addEventListener('shopify:block:deselect', (e) => {
        if (!this.contains(e.target)) return;
        this.editorSelected = false; this.schedule();
      }, options);
      document.addEventListener('shopify:section:load', (e) => {
        if (e.target.contains(this) && !this.swiper) this.build();
      }, options);
      document.addEventListener('shopify:section:unload', (e) => {
        if (e.target.contains(this)) this.teardown();
      }, options);
      this.waitForLibrary(0);
    }

    waitForLibrary(attempt) {
      if (!this.isConnected || this.controller.signal.aborted) return;
      if (window.MinimogLibs?.Swiper) { this.build(); return; }
      // vendor.js is already supplied by this theme. Keep the scrollable fallback if it fails.
      if (attempt < 40) this.libraryTimer = setTimeout(() => this.waitForLibrary(attempt + 1), 50);
    }

    makeDots() {
      if (!this.dots) return;
      this.dots.replaceChildren();
      this.originals.forEach((slide, i) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mos-battery__dot';
        button.setAttribute('aria-label', `Show ${slide.getAttribute('aria-label')}`);
        button.setAttribute('aria-controls', this.viewport.id);
        button.addEventListener('click', () => this.go(i), { signal: this.controller.signal });
        this.dots.append(button);
      });
    }

    build() {
      if (!this.isConnected || !window.MinimogLibs?.Swiper) return;
      this.pause();
      if (this.swiper) { this.index = this.swiper.realIndex; this.swiper.destroy(true, true); this.swiper = null; }
      this.originals.forEach((slide) => { slide.inert = false; slide.removeAttribute('aria-hidden'); });
      this.staticLayout = this.count === 1 || (this.desktop.matches && this.count < 3);
      this.classList.toggle('is-static', this.staticLayout);
      this.classList.add('is-ready');
      if (this.prev) this.prev.hidden = this.staticLayout;
      if (this.next) this.next.hidden = this.staticLayout;
      if (this.dots) this.dots.hidden = this.staticLayout;
      if (this.staticLayout) { this.update(); return; }
      this.swiper = new window.MinimogLibs.Swiper(this.viewport, {
        wrapperClass: 'mos-battery__track',
        slideClass: 'mos-battery__slide',
        slideActiveClass: 'mos-battery__slide--active',
        slideDuplicateActiveClass: 'mos-battery__slide--duplicate-active',
        slideVisibleClass: 'mos-battery__slide--visible',
        slidesPerView: 1.12,
        centeredSlides: true,
        spaceBetween: 18,
        initialSlide: this.index,
        loop: this.count >= 3,
        loopedSlides: this.count,
        speed: this.reduced.matches ? 0 : 400,
        simulateTouch: true,
        threshold: 7,
        touchStartPreventDefault: false,
        slideToClickedSlide: false,
        preventClicks: true,
        preventClicksPropagation: true,
        watchSlidesProgress: true,
        watchSlidesVisibility: true,
        resizeObserver: true,
        breakpoints: {
          768: { slidesPerView: 1.65, spaceBetween: 24 },
          1024: { slidesPerView: 3, spaceBetween: 28 }
        },
        on: {
          init: () => queueMicrotask(() => this.update()),
          slideChange: () => { this.pause(); queueMicrotask(() => this.update(false)); },
          transitionEnd: () => { this.update(); this.schedule(); },
          touchStart: () => this.pause(),
          sliderFirstMove: () => { this.dragMoved = true; this.classList.add('is-dragging'); },
          touchEnd: () => { if (this.dragMoved) this.suppressUntil = performance.now() + 350; this.dragging = false; this.classList.remove('is-dragging'); this.schedule(); },
          resize: () => this.update()
        }
      });
      this.update();
      this.schedule();
    }

    update(accessibility = true) {
      if (!this.isConnected || !this.viewport) return;
      if (this.swiper) this.index = this.swiper.realIndex;
      [...(this.dots?.children || [])].forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
      if (this.prev) this.prev.disabled = !this.staticLayout && !this.swiper?.params.loop && !!this.swiper?.isBeginning;
      if (this.next) this.next.disabled = !this.staticLayout && !this.swiper?.params.loop && !!this.swiper?.isEnd;
      if (!accessibility) return;
      const viewport = this.viewport.getBoundingClientRect();
      this.querySelectorAll('.mos-battery__slide').forEach((slide) => {
        const r = slide.getBoundingClientRect();
        const visible = this.staticLayout || Math.min(r.right, viewport.right) - Math.max(r.left, viewport.left) > r.width * .2;
        slide.inert = !visible;
        slide.setAttribute('aria-hidden', String(!visible));
        if (slide.classList.contains('swiper-slide-duplicate')) slide.removeAttribute('data-shopify-editor-block');
      });
      const live = this.querySelector('.mos-battery__status');
      if (live) live.textContent = `${this.index + 1} / ${this.count}`;
      if (this.focusAfterMove && this.swiper) {
        this.focusAfterMove = false;
        this.swiper.slides[this.swiper.activeIndex]?.querySelector('.mos-battery__buy')?.focus({ preventScroll: true });
      }
    }

    go(index) {
      if (!this.swiper || this.staticLayout || this.swiper.animating) return;
      this.pause();
      const next = Math.min(this.count - 1, Math.max(0, index));
      if (this.swiper.params.loop) this.swiper.slideToLoop(next);
      else this.swiper.slideTo(next);
      this.update(); this.schedule();
    }

    move(direction) {
      if (!this.swiper || this.staticLayout || this.swiper.animating) return;
      this.pause();
      if (direction > 0) this.swiper.slideNext(); else this.swiper.slidePrev();
      this.update(false); this.schedule();
    }

    keydown(event) {
      if (event.altKey || event.ctrlKey || event.metaKey || event.target.closest('input,textarea,select')) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key) || !this.swiper) return;
      event.preventDefault();
      this.focusAfterMove = !!event.target.closest('.mos-battery__card');
      if (event.key === 'Home') this.go(0);
      else if (event.key === 'End') this.go(this.count - 1);
      else this.move(event.key === 'ArrowRight' ? 1 : -1);
    }

    pause() { clearTimeout(this.autoplayTimer); }
    schedule() {
      this.pause();
      if (!this.isConnected || this.dataset.autoplay !== 'true' || this.reduced.matches || this.staticLayout || !this.swiper || this.hovered || this.dragging || this.editorSelected || this.contains(document.activeElement) || document.hidden || window.Shopify?.designMode) return;
      this.autoplayTimer = setTimeout(() => {
        if (!this.swiper) return;
        if (!this.swiper.params.loop && this.swiper.isEnd) this.go(0); else this.move(1);
      }, Math.max(3000, Number(this.dataset.interval) || 5000));
    }

    teardown() {
      this.controller?.abort();
      clearTimeout(this.libraryTimer);
      this.pause();
      this.swiper?.destroy(true, true);
      this.swiper = null;
      this.classList.remove('is-ready', 'is-static', 'is-dragging');
    }
    disconnectedCallback() { this.teardown(); }
  });
}
